import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { STAGES, STAGE_LABELS, scoreColor, fmtRM } from "@/lib/constants";
import { Badge } from "@/components/ui/badge";
import { KanbanSquare, Flame, GripVertical } from "lucide-react";

const HEADER_TONE = {
  NEW: "border-t-slate-400", AI_QUALIFIED: "border-t-violet-500", ASSIGNED: "border-t-sky-500",
  CONTACTED: "border-t-cyan-500", QUOTATION: "border-t-amber-500", FOLLOW_UP: "border-t-orange-500",
  WON: "border-t-emerald-500", LOST: "border-t-rose-500",
};

export default function Pipeline() {
  const qc = useQueryClient();
  const { data: board } = useQuery({ queryKey: ["pipeline"], queryFn: () => api.get("/pipeline").then((r) => r.data) });

  const onDrop = async (e, stage) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("cid");
    if (!id) return;
    await api.put(`/pipeline/${id}/stage`, { stage });
    toast.success(`Lead dipindah ke ${STAGE_LABELS[stage]}`);
    qc.invalidateQueries({ queryKey: ["pipeline"] });
  };

  if (!board) return <div className="p-6"><div className="h-8 w-40 bg-secondary rounded animate-pulse" /></div>;

  return (
    <div className="p-6 space-y-5" data-testid="pipeline-page">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><KanbanSquare className="h-6 w-6 text-primary" /> Sales Pipeline</h1>
        <p className="text-muted-foreground text-sm mt-1">Seret lead antara stage. AI qualify secara automatik dari WhatsApp.</p>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4" data-testid="pipeline-kanban-board">
        {STAGES.map((stage) => {
          const col = board[stage] || { contacts: [], count: 0, value: 0 };
          return (
            <div key={stage} onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(e, stage)}
              data-testid={`pipeline-stage-${stage.toLowerCase().replace(/_/g, "-")}`}
              className={`w-72 shrink-0 rounded-2xl border border-border border-t-4 ${HEADER_TONE[stage]} bg-card flex flex-col`}>
              <div className="p-3 border-b border-border flex items-center justify-between">
                <span className="font-display font-semibold text-sm">{STAGE_LABELS[stage]}</span>
                <div className="text-right">
                  <span className="text-xs bg-secondary px-2 py-0.5 rounded-full font-medium">{col.count}</span>
                  {col.value > 0 && <p className="text-[10px] text-muted-foreground mt-0.5">{fmtRM(col.value)}</p>}
                </div>
              </div>
              <div className="p-2 space-y-2 flex-1 min-h-[120px]">
                {col.contacts.map((c) => (
                  <div key={c.id} draggable onDragStart={(e) => e.dataTransfer.setData("cid", c.id)}
                    data-testid={`pipeline-card-${c.id}`}
                    className="rounded-xl border border-border bg-background p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow group">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-xs shrink-0">{c.name[0]}</div>
                        <div className="min-w-0">
                          <p className="font-semibold text-sm truncate">{c.name}</p>
                          <p className="text-[11px] text-muted-foreground truncate">{c.interest}</p>
                        </div>
                      </div>
                      <GripVertical className="h-4 w-4 text-muted-foreground/40 opacity-0 group-hover:opacity-100 shrink-0" />
                    </div>
                    <div className="flex items-center justify-between mt-2.5">
                      <Badge className={`${scoreColor(c.lead_score)} border-0 rounded-full text-[10px]`}>
                        {c.lead_score >= 85 && <Flame className="h-3 w-3 mr-0.5" />}{c.lead_score}
                      </Badge>
                      {c.revenue > 0 && <span className="text-xs font-semibold text-primary">{fmtRM(c.revenue)}</span>}
                    </div>
                  </div>
                ))}
                {col.contacts.length === 0 && <p className="text-center text-xs text-muted-foreground py-6">Kosong</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
